import React from 'react';
import PropTypes from 'prop-types';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useBooleanState } from '../../hooks';
import PendingDocumentsDialog from '../dialogs/PendingDocumentsDialog';
import { transformOnlyPendingReviewDocumentsToRestrictions } from '../../helpers/review-documents-transformer';

const PendingDocumentsManagerCell = ({ invoice, type }) => {
  if (['PENDINGDOCUMENTSEXPIRED', 'REJECTEDPREOFFEREVALUATION'].includes(invoice.status)) return null;
  if (
    ['EVALUATING', 'DOCUMENTSREVIEWED'].includes(
      invoice.preoffer?.preofferevaluationrequest?.status,
    )
  ) {
    return null;
  }
  const [openDialog, setOpenDialog] = useBooleanState();
  const filteredRestrictions = invoice.restrictedBy?.filter(
    (res) => ['CREATED', 'PENDING'].includes(res.status)
      && res.restrictionType === type
      && res.restriction !== 'OTHER',
  ) || [];

  const reviewRestrictions = invoice?.reviewDocumentsManager
    ? transformOnlyPendingReviewDocumentsToRestrictions(invoice.reviewDocumentsManager)
      .filter((res) => res.restrictionType === type)
    : [];

  const allRestrictions = [...filteredRestrictions, ...reviewRestrictions];
  const avatarBgColor = () => {
    if (!allRestrictions.length) return '';
    if (type === 'Risk') return 'primary.main';
    return '#ff9800';
  };
  const tootlipLabel = {
    Risk: 'Se están revisando tus documentos pendientes',
    Operations: 'Esta factura no tiene condiciones pendientes para girar',
  };
  return (
    <>
      <Tooltip
        title={
          !allRestrictions.length ? (
            <Typography>{tootlipLabel[type]}</Typography>
          ) : null
        }
      >
        <Avatar
          onClick={() => {
            if (allRestrictions.length) {
              setOpenDialog();
            }
          }}
          disabled={!allRestrictions.length}
          sx={{
            width: 26,
            height: 26,
            bgcolor: avatarBgColor(),
            ...(allRestrictions.length && {
              cursor: 'pointer',
              '&:hover': {
                transform: 'scale(1.1)',
              },
              transition: 'transform 0.15s ease-in-out',
            }),
          }}
        >
          <Typography variant="subtitle2">
            {allRestrictions.length}
          </Typography>
        </Avatar>
      </Tooltip>
      {openDialog && (
        <PendingDocumentsDialog
          invoiceId={invoice.id}
          open={openDialog}
          onClose={setOpenDialog}
          type={type}
          filteredRestrictions={allRestrictions}
          executiveAssigned={invoice.company.executiveAssigned}
        />
      )}
    </>
  );
};

const detail = PropTypes.shape({
  id: PropTypes.string.isRequired,
  restrictionLabel: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
});

PendingDocumentsManagerCell.propTypes = {
  type: PropTypes.oneOf(['Risk', 'Operations']).isRequired,
  invoice: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    company: PropTypes.shape({
      id: PropTypes.string.isRequired,
      executiveAssigned: PropTypes.shape({
        id: PropTypes.string.isRequired,
        completeName: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
        phoneNumber: PropTypes.string.isRequired,
      }),
    }).isRequired,
    preoffer: PropTypes.shape({
      id: PropTypes.string,
      preofferevaluationrequest: PropTypes.shape({
        id: PropTypes.string,
        status: PropTypes.string,
      }),
    }),
    reviewDocumentsManager: PropTypes.shape({
      id: PropTypes.string,
      requiresOc: PropTypes.bool,
      requiresHes: PropTypes.bool,
      requiresPaymentStatus: PropTypes.bool,
      requiresPortalScreenshot: PropTypes.bool,
      hasReceptionAcknowledgment: PropTypes.bool,
      ocReviewState: PropTypes.string,
      hesReviewState: PropTypes.string,
      paymentStatusReviewState: PropTypes.string,
      portalScreenshotReviewState: PropTypes.string,
      receptionAcknowledgmentReviewState: PropTypes.string,
      createdAt: PropTypes.string,
    }),
    restrictedBy: PropTypes.arrayOf(detail),
  }).isRequired,
};

export default PendingDocumentsManagerCell;
